import React, { useState } from 'react';
import { 
  Share2, 
  Link as LinkIcon,
  Check
} from 'lucide-react';

interface SocialShareProps {
  title: string;
  url: string;
  description?: string;
  image?: string;
}

const SocialShare: React.FC<SocialShareProps> = ({ 
  title, 
  url, 
  description = '', 
  image = 'https://losciclosdemarha.uy/images/share-og.jpg' 
}) => {
  const [copied, setCopied] = useState(false);

  const shareData = {
    title,
    text: description,
    url,
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Error al copiar enlace:', err);
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.error('Error al compartir:', err);
      }
    }
  };

  const shareToFacebook = () => {
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    window.open(facebookUrl, '_blank', 'width=600,height=400');
  };

  const shareToTwitter = () => {
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(url)}`;
    window.open(twitterUrl, '_blank', 'width=600,height=400');
  };

  const shareToWhatsApp = () => {
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`;
    window.open(whatsappUrl, '_blank');
  };

  const shareToLinkedIn = () => {
    const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
    window.open(linkedinUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="mt-8 pt-6 border-t border-gray-200">
      <div className="flex flex-col items-center">
        <h3 className="text-lg font-semibold text-contessa-800 mb-4">
          ¿Te gustó este artículo? ¡Compártelo!
        </h3>
        
        <div className="flex flex-wrap justify-center gap-3">
          {/* Compartir nativo (móvil) */}
          {typeof navigator.share === 'function' && (
            <button
              onClick={handleNativeShare}
              className="flex items-center gap-2 p-2 bg-contessa-600 text-white rounded-lg hover:bg-contessa-700 transition-colors"
              title="Compartir"
            >
              <Share2 size={18} />
            </button>
          )}

          {/* WhatsApp */}
          <button
            onClick={shareToWhatsApp}
            className="flex items-center gap-2 p-2 rounded-lg transition-all duration-200 hover:scale-110"
            title="Compartir en WhatsApp"
          >
            <img 
              src="/images/icons/whatsapp.svg" 
              alt="WhatsApp" 
              className="w-5 h-5"
              onError={(e) => {
                // Fallback a icono de texto si no carga el SVG
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextElementSibling?.classList.remove('hidden');
              }}
            />
          </button>

          {/* Facebook */}
          <button
            onClick={shareToFacebook}
            className="flex items-center gap-2 p-2 rounded-lg transition-all duration-200 hover:scale-110"
            title="Compartir en Facebook"
          >
            <img 
              src="/images/icons/facebook.svg" 
              alt="Facebook" 
              className="w-5 h-5"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextElementSibling?.classList.remove('hidden');
              }}
            />
          </button>

          {/* LinkedIn */}
          <button
            onClick={shareToLinkedIn}
            className="flex items-center gap-2 p-2 rounded-lg transition-all duration-200 hover:scale-110"
            title="Compartir en LinkedIn"
          >
            <img 
              src="/images/icons/linkedin.svg" 
              alt="LinkedIn" 
              className="w-5 h-5"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextElementSibling?.classList.remove('hidden');
              }}
            />
          </button>

          {/* Copiar enlace */}
          <button
            onClick={handleCopyLink}
            className={`flex items-center gap-2 p-2 bg-contessa-600 rounded-lg transition-colors ${
              copied 
                ? 'bg-contessa-600 text-white' 
                : 'bg-contessa-600 text-white hover:bg-contessa-700'
            }`}
            title="Copiar enlace"
          >
            {copied ? <Check size={18}/> : <LinkIcon size={18} />}
            {copied && <span className="text-sm">¡Copiado!</span>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SocialShare;